import * as React from 'react';
import {FastFieldProps} from 'formik/dist/FastField';

import {TemplateRCDataFromRequest} from 'core/RateConfirmation/types';
import {getLogoAlt, getLogoUrl} from 'core/RateConfirmation/modules/logo';
import {getTranslateSomeTitle, isTranslatePL} from 'core/RateConfirmation/modules/translate';
import {getTransformDataToTemplate} from 'core/RateConfirmation/modules/transformDataToTemplate';
import {isDriverDetails} from 'core/RateConfirmation/modules/driverDetails';

import {getDescInfo} from './utils';

import StopsInfo from '../common/StopsInfo';
import Requirements from '../common/Requirements';
import Footer from '../common/Footer';

import styles from './styles.module.scss';

type OwnProps = {
    data: TemplateRCDataFromRequest;
    children: React.ReactElement<FastFieldProps>;
};

const TemplateEU: React.FC<OwnProps> = ({data, children}) => {
    const urlCompanyLogo = getLogoUrl(data);
    const altCompany = getLogoAlt(data);
    const {contactName, contactPhone, contactEmail, sentBy} = getDescInfo(data);
    const isTranslatePolish = isTranslatePL(data);
    const isShowDriverDetails = isDriverDetails(data);
    const {currentCarrier, rcCarrier, travelOrder, loadNumber, titles, stopsData, totalRate, driverInfo, serviceAlert} =
        getTransformDataToTemplate(data);

    const titleBlock = getTranslateSomeTitle({
        data,
        translateTitle: 'Confirmation of Contract Carrier Agreement',
    });
    const titleStopBlock = getTranslateSomeTitle({
        data,
        translateTitle: 'Order details',
    });
    const titleRequirementsBlock = getTranslateSomeTitle({
        data,
        translateTitle: 'Requirements',
    });

    return (
        <>
            <div className={styles.header}>
                <div className={styles.header_main}>
                    <div className={styles.header_logo}>
                        <img src={urlCompanyLogo} alt={altCompany} />
                    </div>
                    <div className={styles.header_info}>
                        <div className={styles.header_info__title}>
                            <strong>{currentCarrier.companyName}</strong>
                        </div>
                        <div className={styles.header_row}>
                            <div className={styles.header_col}>{currentCarrier.nip.title}</div>
                            <div className={styles.header_col}>{currentCarrier.nip.value}</div>
                        </div>
                        <div className={styles.header_row}>
                            <div className={styles.header_col}>{currentCarrier.address.title}</div>
                            <div className={styles.header_col}>{currentCarrier.address.value}</div>
                        </div>
                        {currentCarrier.phone.value ? (
                            <div className={styles.header_row}>
                                <div className={styles.header_col}>{currentCarrier.phone.title}</div>
                                <div className={styles.header_col}>{currentCarrier.phone.value}</div>
                            </div>
                        ) : null}
                        {currentCarrier.fax.value ? (
                            <div className={styles.header_row}>
                                <div className={styles.header_col}>{currentCarrier.fax.title}</div>
                                <div className={styles.header_col}>{currentCarrier.fax.value}</div>
                            </div>
                        ) : null}

                        <div className={styles.header_row__border}>
                            <div className={styles.header_col}>{currentCarrier.mailingAddress.title}</div>
                            <div className={styles.header_col}>
                                <strong>{currentCarrier.mailingAddress.value}</strong>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.header_desc}>
                    <div className={styles.header_contact}>
                        <div>
                            <strong>{contactName.title}</strong> {contactName.value}
                        </div>
                        <div>
                            <strong>{contactPhone.title}</strong> {contactPhone.value}
                        </div>
                        <div>
                            <strong>{contactEmail.title}</strong> {contactEmail.value}
                        </div>
                    </div>
                    <div className={styles.header_sendBy}>
                        <strong>{sentBy.title} </strong>
                        {sentBy.value}
                    </div>
                </div>
            </div>
            <div className={styles.main}>
                <h1>{titleBlock}</h1>
                <div className={styles.numbers}>
                    <p>
                        <strong>{travelOrder.title}</strong> {travelOrder.value} | <strong>{loadNumber.title}</strong>{' '}
                        {loadNumber.value}
                    </p>
                </div>
                <div className={styles.companies}>
                    <h2>1. {rcCarrier.titleBlock}</h2>
                    <div className={styles.companies_row}>
                        <div className={styles.companies_col}>
                            <div className={styles.company_title}>
                                <strong>{rcCarrier.name}</strong>
                            </div>
                            <table className={styles.company_table}>
                                <tbody>
                                    <tr>
                                        <td>{rcCarrier.address.title}</td>
                                        <td>{rcCarrier.address.value}</td>
                                    </tr>
                                    <tr>
                                        <td>{rcCarrier.phone.title}</td>
                                        <td>{rcCarrier.phone.value}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        {isShowDriverDetails ? (
                            <div className={styles.companies_col}>
                                <table className={styles.company_table}>
                                    <tbody>
                                        <tr>
                                            <td>{driverInfo.name.title}</td>
                                            <td>{driverInfo.name.value}</td>
                                        </tr>
                                        <tr>
                                            <td>{driverInfo.vehicleSize.title}</td>
                                            <td>{driverInfo.vehicleSize.value}</td>
                                        </tr>
                                        <tr>
                                            <td>{driverInfo.vehicleNumber.title}</td>
                                            <td>{driverInfo.vehicleNumber.value}</td>
                                        </tr>
                                        {driverInfo.phone.value ? (
                                            <tr>
                                                <td>{driverInfo.phone.title}</td>
                                                <td>{driverInfo.phone.value}</td>
                                            </tr>
                                        ) : null}
                                    </tbody>
                                </table>
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className={styles.stops}>
                    <h2>2. {titleStopBlock}</h2>
                    <table className={styles.stops_table}>
                        <thead>
                            <tr>
                                <th>{titles.stop}</th>
                                <th>{titles.time}</th>
                                <th>{titles.details}</th>
                            </tr>
                        </thead>
                        <StopsInfo stopsInfo={stopsData} />
                    </table>
                </div>
                <div className={styles.total}>
                    <h3>{totalRate.titleBlock}</h3>
                    <div className={styles.total_main}>
                        <div className={styles.total_row}>
                            <div className={styles.total_col}>
                                <strong>{totalRate.pieces.title}</strong> {totalRate.pieces.value}
                            </div>
                            <div className={styles.total_col}>
                                <strong>{totalRate.weight.title}</strong> {totalRate.weight.value}
                            </div>
                        </div>
                        <div className={styles.total_row}>
                            <div className={styles.total_col}>
                                <strong>{totalRate.rate.title}</strong> {totalRate.rate.value}
                            </div>
                            <div className={styles.total_col}>
                                <strong>{totalRate.titleDesc}</strong> {children}
                            </div>
                        </div>
                    </div>
                </div>
                {serviceAlert?.map((importantMassage, index) => (
                    <div key={index} className={styles.important}>
                        {importantMassage}
                    </div>
                ))}
                <h2>3. {titleRequirementsBlock}</h2>
                <Requirements data={data} withWarn={isTranslatePolish} />
            </div>
            <Footer data={data} isNATemplate={false} />
        </>
    );
};

export default TemplateEU;
